import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod } from '@vision/ui/enums';
import { createHttpExtraOptions } from '@vision/ui/utils/http.utils';
import { axiosBaseQuery } from '../base.service';
import type {
  ChannelThemeAssetResponseDto,
  ChannelThemeResponseDto,
  ChannelThemeSummaryResponseDto,
  ChannelThemesResponseDto,
} from './channel-themes.api.types';
import {
  fromApiChannelTheme,
  fromApiChannelThemeAsset,
  fromApiChannelThemes,
  fromApiChannelThemeSummary,
  toApiChannelThemeAssetUpload,
  toApiChannelThemeAssetUploadUrl,
  toApiChannelThemeUrl,
  toApiChannelThemesUrl,
  toApiChannelThemeWrite,
} from './channel-themes.mapper';
import type { ThemeAssetReference } from '../theme-contract/types';
import type {
  AccountTheme,
  AccountThemeList,
  AccountThemeSummary,
  CloneChannelThemeArgs,
  CreateChannelThemeArgs,
  DeleteChannelThemeArgs,
  GetChannelThemeArgs,
  GetChannelThemesArgs,
  UpdateChannelThemeArgs,
  UploadChannelThemeAssetArgs,
} from './types';

// The theme editor reports a successful write on the selected theme in its preview header.
function toWriteOptions(notifySuccess = true) {
  return notifySuccess ? undefined : createHttpExtraOptions({ notification: { autoHandleSuccess: false } });
}

// The account themes the channels' Theme Designer selects from.
export const ChannelThemesService = createApi({
  reducerPath: 'ChannelThemesService',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['ChannelThemes'],
  endpoints: (builder) => ({
    // The list leaves out each theme's document; selecting a theme reads it with `getChannelTheme`.
    getChannelThemes: builder.query<AccountThemeList, GetChannelThemesArgs>({
      query: ({ accountId }) => ({
        url: toApiChannelThemesUrl(accountId),
        method: HttpMethod.GET,
      }),
      providesTags: ['ChannelThemes'],
      transformResponse: (response: ChannelThemesResponseDto) => fromApiChannelThemes(response),
    }),
    // The caller reports a failure.
    getChannelTheme: builder.query<AccountTheme, GetChannelThemeArgs>({
      query: ({ accountId, themeId }) => ({
        url: toApiChannelThemeUrl(accountId, themeId),
        method: HttpMethod.GET,
        extraOptions: createHttpExtraOptions({ notification: { autoHandleError: false } }),
      }),
      providesTags: ['ChannelThemes'],
      transformResponse: (response: ChannelThemeResponseDto) => fromApiChannelTheme(response.data),
    }),
    createChannelTheme: builder.mutation<AccountTheme, CreateChannelThemeArgs>({
      query: (args) => ({
        url: toApiChannelThemesUrl(args.accountId),
        method: HttpMethod.POST,
        data: toApiChannelThemeWrite(args),
        extraOptions: toWriteOptions(args.notifySuccess),
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['ChannelThemes']),
      transformResponse: (response: ChannelThemeResponseDto) => fromApiChannelTheme(response.data),
    }),
    updateChannelTheme: builder.mutation<AccountThemeSummary, UpdateChannelThemeArgs>({
      query: (args) => ({
        url: toApiChannelThemeUrl(args.accountId, args.themeId, 'update'),
        method: HttpMethod.POST,
        data: toApiChannelThemeWrite(args),
        extraOptions: toWriteOptions(args.notifySuccess),
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['ChannelThemes']),
      transformResponse: (response: ChannelThemeSummaryResponseDto) => fromApiChannelThemeSummary(response.data),
    }),
    cloneChannelTheme: builder.mutation<AccountTheme, CloneChannelThemeArgs>({
      query: ({ accountId, themeId }) => ({
        url: toApiChannelThemeUrl(accountId, themeId, 'clone'),
        method: HttpMethod.POST,
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['ChannelThemes']),
      transformResponse: (response: ChannelThemeResponseDto) => fromApiChannelTheme(response.data),
    }),
    // The API rejects deleting a theme assigned to any channel (HTTP 400).
    deleteChannelTheme: builder.mutation<void, DeleteChannelThemeArgs>({
      query: ({ accountId, themeId }) => ({
        url: toApiChannelThemeUrl(accountId, themeId, 'delete'),
        method: HttpMethod.POST,
        extraOptions: createHttpExtraOptions({
          notification: { translationMessageKey: 'http.message.channel.deleteTheme.{{status}}' },
        }),
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['ChannelThemes']),
    }),
    // A chosen image is uploaded right away; its thumbnail in the editor reports success.
    uploadChannelThemeAsset: builder.mutation<ThemeAssetReference, UploadChannelThemeAssetArgs>({
      query: ({ accountId, file }) => ({
        url: toApiChannelThemeAssetUploadUrl(accountId),
        method: HttpMethod.POST,
        data: toApiChannelThemeAssetUpload(file),
        extraOptions: toWriteOptions(false),
      }),
      transformResponse: (response: ChannelThemeAssetResponseDto) => fromApiChannelThemeAsset(response.data),
    }),
  }),
});

export const {
  useCloneChannelThemeMutation,
  useCreateChannelThemeMutation,
  useDeleteChannelThemeMutation,
  useGetChannelThemesQuery,
  useLazyGetChannelThemeQuery,
  useUpdateChannelThemeMutation,
  useUploadChannelThemeAssetMutation,
} = ChannelThemesService;
